import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useParticleTemplates } from '../hooks/useParticleTemplates';

export default function ParticleSystem({ template, color, handScale = 1.0 }) {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const particleSystemRef = useRef(null);
  const animationFrameRef = useRef(null);
  const handScaleRef = useRef(1.0);
  const targetScaleRef = useRef(1.0);
  
  const { createTemplate } = useParticleTemplates();

  useEffect(() => {
    if (!containerRef.current) return;

    // Initialize Three.js scene
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    containerRef.current.appendChild(renderer.domElement);

    camera.position.z = 5;

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);
    const pointLight = new THREE.PointLight(0xffffff, 1);
    pointLight.position.set(5, 5, 5);
    scene.add(pointLight);

    sceneRef.current = scene;
    rendererRef.current = renderer;
    cameraRef.current = camera;

    // Handle resize
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (containerRef.current && renderer.domElement) {
        containerRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, []);

  // Update particle system when template or color changes
  useEffect(() => {
    if (!sceneRef.current) return;

    const scene = sceneRef.current;
    const currentColor = new THREE.Color(color);

    // Remove old particle system
    if (particleSystemRef.current) {
      scene.remove(particleSystemRef.current);
      particleSystemRef.current.geometry.dispose();
      particleSystemRef.current.material.dispose();
    }

    // Create new particle system
    const templateData = createTemplate(template, currentColor);
    
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(templateData.positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(templateData.colors, 3));

    const material = new THREE.PointsMaterial({
      size: 0.05,
      vertexColors: true,
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true,
    });

    const particleSystem = new THREE.Points(geometry, material);
    scene.add(particleSystem);
    particleSystemRef.current = particleSystem;
  }, [template, color, createTemplate]);

  // Animation loop
  useEffect(() => {
    if (!rendererRef.current || !sceneRef.current || !cameraRef.current) return;

    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);

      // Smooth scale interpolation
      handScaleRef.current += (targetScaleRef.current - handScaleRef.current) * 0.1;

      if (particleSystemRef.current) {
        // Apply scale from hand tracking
        const scale = handScale || handScaleRef.current;
        particleSystemRef.current.scale.set(scale, scale, scale);
        
        // Rotate particles slowly
        particleSystemRef.current.rotation.y += 0.002;
        particleSystemRef.current.rotation.x += 0.001;

        // Animate particles based on template
        if (template === 'fireworks') {
          const positions = particleSystemRef.current.geometry.attributes.position.array;
          const time = Date.now() * 0.001;

          for (let i = 0; i < positions.length; i += 3) {
            const angle = (i / 3) * 0.01 + time;
            const radius = 0.5 + Math.sin(angle) * 0.5;
            positions[i] = radius * Math.cos(angle * 2);
            positions[i + 1] = radius * Math.sin(angle * 2);
          }

          particleSystemRef.current.geometry.attributes.position.needsUpdate = true;
        }
      }

      rendererRef.current.render(sceneRef.current, cameraRef.current);
    };

    animate();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [template, handScale]);

  // Update target scale from hand tracking
  useEffect(() => {
    targetScaleRef.current = handScale || 1.0;
  }, [handScale]);

  return <div id="canvas-container" ref={containerRef} />;
}

